import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/build/pdf.min.js";
import { FREE_RESOURCES } from "@/lib/site";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js";

function LoadingNote({ children }) {
  return (
    <div className="mt-16 text-center">
      <FileText className="w-8 h-8 text-gold mx-auto animate-pulse" />
      <p className="mt-4 text-sm text-mist">{children}</p>
    </div>
  );
}

export default function PdfPreview() {
  const { resourceId } = useParams();
  const resource = FREE_RESOURCES.find((r) => r.id === resourceId);
  const [status, setStatus] = useState("loading");
  const pagesRef = useRef(null);

  useEffect(() => {
    if (!resource) return;
    let cancelled = false;
    const container = pagesRef.current;
    const render = async () => {
      try {
        const pdf = await pdfjsLib.getDocument(resource.file).promise;
        if (cancelled) return;
        setStatus("ready");
        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const fit = (container.clientWidth - 8) / base.width;
          const dpr = window.devicePixelRatio || 1;
          const viewport = page.getViewport({
            scale: Math.min(fit, 2) * dpr,
          });
          const canvas = document.createElement("canvas");
          canvas.setAttribute("data-page", String(n));
          canvas.style.width = "100%";
          canvas.style.display = "block";
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const wrapper = document.createElement("div");
          wrapper.setAttribute("data-testid", `pdf-preview-page-${n}`);
          wrapper.className =
            "bg-white border border-line rounded-md overflow-hidden shadow-[0_18px_36px_-20px_rgba(11,21,34,0.35)]";
          wrapper.appendChild(canvas);
          container.appendChild(wrapper);
          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          }).promise;
        }
      } catch (err) {
        if (!cancelled) setStatus("error");
      }
    };
    render();
    return () => {
      cancelled = true;
      if (pagesRef.current) pagesRef.current.innerHTML = "";
    };
  }, [resource]);

  if (!resource || !resource.file) {
    return (
      <div className="bg-ivory min-h-screen" data-testid="pdf-preview-page">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-40 pb-24 text-center">
          <p className="font-serif text-3xl text-navy">
            That guide could not be found.
          </p>
          <Link
            to="/free-resources"
            data-testid="pdf-preview-back"
            className="mt-8 inline-flex items-center gap-2 bg-navy text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Free Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ivory min-h-screen" data-testid="pdf-preview-page">
      <div className="sticky top-16 sm:top-[72px] z-40 bg-ivory/95 backdrop-blur-md border-b border-line">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-3 flex items-center justify-between gap-4">
          <Link
            to="/free-resources"
            data-testid="pdf-preview-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-stone2 hover:text-navy transition-colors duration-200 whitespace-nowrap"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Back to Free Resources</span>
          </Link>
          <a
            href={resource.file}
            download
            data-testid="pdf-preview-download"
            className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-gold-hover hover:text-ivory transition-colors duration-200 whitespace-nowrap"
          >
            Download PDF <Download className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-10">
        <h1 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
          {resource.title}
        </h1>
        {resource.subtitle && (
          <p className="mt-2 font-serif italic text-gold-hover">
            {resource.subtitle}
          </p>
        )}
        <p className="mt-3 text-xs uppercase tracking-[0.18em] text-mist">
          Free PDF preview — no sign-up required
        </p>

        <div ref={pagesRef} data-testid="pdf-preview-pages" className="mt-8 space-y-6" />

        {status === "loading" && <LoadingNote>Preparing your preview…</LoadingNote>}
        {status === "error" && (
          <LoadingNote>
            The preview could not be displayed in this browser. You can still
            download the guide above.
          </LoadingNote>
        )}
        {status === "ready" && (
          <div className="mt-10 text-center">
            <a
              href={resource.file}
              download
              data-testid="pdf-preview-download-bottom"
              className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-gold-hover hover:text-ivory transition-colors duration-200"
            >
              Download PDF <Download className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
